import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Typography, useTheme, Paper } from "@mui/material";
import { FoodsIcon, FoodsTooltip } from "@foodpilot/foods";
import Close from "@mui/icons-material/Close";
import Fullscreen from "@mui/icons-material/Fullscreen";
import FullscreenExit from "@mui/icons-material/FullscreenExit";
import { getMimeTypeLabel } from "src/utils/files";
import { MediaItem } from "../context";

type FilePreviewModalProps = {
    open: boolean;
    onClose: () => void;
    mediaItem: MediaItem | null;
    onDownload: (item: MediaItem) => void;
};

export const FilePreviewModal = (props: FilePreviewModalProps) => {
    const { open, onClose, mediaItem, onDownload } = props;
    const { t } = useTranslation();
    const theme = useTheme();
    const [isFullscreen, setIsFullscreen] = useState(false);

    const toggleFullscreen = () => {
        setIsFullscreen(!isFullscreen);
    };

    if (!mediaItem) return null;

    const isImage = mediaItem.mimeType?.startsWith("image/");
    const isVideo = mediaItem.mimeType?.startsWith("video/");
    const isPdf = mediaItem.mimeType === "application/pdf";

    const handleClosePreview = () => {
        onClose();
        setIsFullscreen(false);
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            maxWidth="lg"
            fullWidth
            fullScreen={isFullscreen}
            PaperProps={{
                sx: {
                    height: isFullscreen ? "100%" : "80vh",
                    width: "100%",
                    borderRadius: isFullscreen ? "0" : theme.shape.borderRadius,
                    maxWidth: isFullscreen ? "100%" : "1200px",
                    m: isFullscreen ? 0 : theme.spacing(2),
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    borderBottom: `1px solid ${theme.palette.divider}`,
                    pb: theme.spacing(1),
                }}
            >
                <Box sx={{ display: "flex", alignItems: "center", gap: theme.spacing(1) }}>
                    <Typography variant="h6" component="div">
                        {mediaItem.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: theme.spacing(2) }}>
                        {getMimeTypeLabel(mediaItem.mimeType)}
                    </Typography>
                </Box>
                <Box>
                    <FoodsTooltip
                        title={
                            isFullscreen ?
                                t("common.exit_fullscreen", "Exit fullscreen")
                            :   t("common.fullscreen", "Fullscreen")
                        }
                    >
                        <IconButton onClick={toggleFullscreen} size="small">
                            {isFullscreen ?
                                <FullscreenExit />
                            :   <Fullscreen />}
                        </IconButton>
                    </FoodsTooltip>
                    <FoodsTooltip title={t("common.close", "Close")}>
                        <IconButton onClick={handleClosePreview} size="small">
                            <Close />
                        </IconButton>
                    </FoodsTooltip>
                </Box>
            </DialogTitle>
            <DialogContent
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    overflow: "auto",
                    p: isFullscreen ? theme.spacing(4) : theme.spacing(2),
                    height: "100%",
                }}
            >
                {isImage && mediaItem.url && (
                    <img
                        src={mediaItem.url}
                        alt={mediaItem.name || t("settings.media_library.preview.image_alt", "Image preview")}
                        style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                            objectFit: "contain",
                        }}
                    />
                )}

                {isVideo && mediaItem.url && (
                    <video
                        controls
                        style={{
                            maxWidth: "100%",
                            maxHeight: "100%",
                        }}
                    >
                        <source src={mediaItem.url} />
                        <track kind="captions" srcLang="en" label="English captions" />
                        {t("settings.media_library.preview.video_not_supported", "Video preview not supported")}
                    </video>
                )}

                {isPdf && mediaItem.url && (
                    <iframe
                        src={mediaItem.url}
                        title={mediaItem.name || t("settings.media_library.preview.pdf_title", "PDF preview")}
                        width="100%"
                        height="100%"
                        style={{ border: "none" }}
                    />
                )}

                {!isImage && !isVideo && !isPdf && (
                    <Paper
                        elevation={2}
                        sx={{
                            p: theme.spacing(4),
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            maxWidth: "400px",
                            textAlign: "center",
                        }}
                    >
                        <Box sx={{ mb: theme.spacing(2), fontSize: "64px" }}>
                            <FoodsIcon icon="addNotes" size={6} />
                        </Box>
                        <Typography variant="body1" gutterBottom>
                            {t("settings.media_library.preview.no_preview_available", "No preview available")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {t("settings.media_library.preview.download_to_view", "Download to view")}
                        </Typography>
                        {mediaItem.canView !== false && (
                            <Box sx={{ mt: theme.spacing(2) }}>
                                <FoodsTooltip title={t("settings.media_library.table.actions.download", "Download")}>
                                    <IconButton onClick={() => onDownload(mediaItem)} color="primary">
                                        <FoodsIcon icon="exportDownload" size={2} />
                                    </IconButton>
                                </FoodsTooltip>
                            </Box>
                        )}
                    </Paper>
                )}
            </DialogContent>
        </Dialog>
    );
};
